import React from "react";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router-dom";

import { ErrorBoundary } from "../atoms/ErrorBoundary";
import { Pages, Categories } from "../constants/Pages";
import { Game } from "../models/Game";
import { User } from "../models/User";
import { UserAuthStatus } from "../models/UserAuthStatus";
import { PageService } from "../services/PageService";
import RecentlyPlayedService from "../services/RecentlyPlayedService";
import { UrlService } from "../services/UrlService";
import { set404Status } from "../store/reducers";
import { SearchTemplate } from "../templates/Search/Search";
import { equalIgnoreCase, isServer } from "../../utils";
import { P404Template } from "../templates/p404/p404";
import { BadgeService } from "../services/BadgeService";

type CategoryPageProps = {
  allGames: Game[];
  user: User;
  userAuthStatus: UserAuthStatus;
  category: Pages | Categories;
  url: string;
  dispatch?: any;
  location: any;
};

interface RootState {
  user: User;
  userAuthStatus: UserAuthStatus;
  games: Game[];
  router: any;
}

class Page extends React.Component<CategoryPageProps> {
  state = {
    games: [],
    isLoading: true,
  };

  matchGames(games: Game[], user: User, category: Pages | Categories): Game[] {
    if (!category) {
      return [];
    }

    if (category === Pages.All) {
      return games;
    }
    return games.filter((game) =>
      game.categories.find((c) => equalIgnoreCase(c, category))
    );
  }

  componentDidMount() {
    BadgeService.updateBadgeStatus();
    this.loadGames();
  }

  componentDidUpdate(prevProps: CategoryPageProps) {
    const needUpdate =
      this.props.user !== prevProps.user ||
      this.props.category !== prevProps.category ||
      this.props.allGames.length !== prevProps.allGames.length ||
      this.props.userAuthStatus !== prevProps.userAuthStatus;

    if (needUpdate) {
      this.loadGames();
    }
  }

  loadGames() {
    const { allGames, user, category } = this.props;

    if (category === Pages.RecentlyPlayed) {
      if (!isServer && this.props.userAuthStatus !== UserAuthStatus.PENDING) {
        RecentlyPlayedService.read(allGames, user).then((games) =>
          this.setState({ games, isLoading: false })
        );
      }
    } else {
      const games = this.matchGames(allGames, user, category);
      this.setState({ games, isLoading: false });
    }
  }

  render() {
    const { allGames, category, user, url } = this.props;

    // Not found category
    if (!category) {
      this.props.dispatch(set404Status(true));
      return <P404Template games={this.state.games} />;
    }

    const list = this.state.games.length
      ? this.state.games
      : this.matchGames(allGames, user, category);

    // Show only games that have defined seo
    const games = list
      .map((g) => new Game(g))
      .filter((g) => g.seo_IsHidden !== true);

    const pageSEO = PageService.getPageSEO(category);

    let isLoading = this.state.isLoading;
    if (isServer) {
      if (category === Pages.RecentlyPlayed) {
        isLoading = true;
      } else {
        isLoading = games.length === 0;
      }
    }

    return (
      <ErrorBoundary>
        <SearchTemplate
          caption={pageSEO.Name}
          games={games}
          categorySEO={pageSEO}
          url={url}
          isLoading={isLoading}
          allGames={allGames}
        />
      </ErrorBoundary>
    );
  }
}

export const Category = connect(
  (state: RootState, props: RouteComponentProps<any>) => ({
    allGames: state.games,
    user: state.user,
    userAuthStatus: state.userAuthStatus,
    category: PageService.findPageSlug(props.match.params.category),
    url: UrlService.buildAbsoluteUrl(props),
    location: state.router.location,
  })
)(Page);
